import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { Printer, ArrowLeft, Loader2, AlertCircle, FileText } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { QuizScript } from '@quebon/shared-learning'
import {
  QuizRenderer,
  LAYOUT,
  STEP4_LAYOUT,
  PAPER_SIZES,
  BLEED_SIZE_MM,
  mmToPx,
  getPageLayout,
  toQuizData,
  PageHeader,
  StepHeader,
  PageFooter,
  ChapterNumber,
  TitleQueture,
  TitleStepSvg,
  PrintQuizItem,
  ImageScaleMultiplierProvider,
  AdditionalImageScalesProvider,
  OptionImageScaleProvider,
  Step4Section,
  MemoPage,
  ModuleSummaryPage,
  TocPage,
  type TocModuleData,
  type TocLesson,
  useQuizMeasurement,
  usePageSplitter,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { printBookApi } from '@/api/printBook'
import LessonPreviewSettingsPanel, {
  getQuizScale,
  getQuizSpacing,
  getQuizAdditionalScalesMap,
  getQuizOptionScale,
} from './LessonPreviewSettingsPanel'
import type { PrintBookLessonSettings } from '@/api/printBook'
import { formatQuizValidationIssues, getQuizValidationIssues, stepLabelOf, type InvalidQuizEntry } from './fullBookPreview/validateSampleVars'

const EMPTY_QUIZZES: QuizScript[] = []

interface PreviewBleedSettings {
  bleedEnabled: boolean
  setBleedEnabled: (enabled: boolean) => void
}

export function LessonPreviewPage() {
  const [searchParams] = useSearchParams()
  const [bleedEnabled, setBleedEnabled] = useState(true)
  const options = { bleedEnabled, setBleedEnabled }

  if (searchParams.get('type') === 'module-summary') {
    return <ModuleSummaryPreview {...options} />
  }
  if (searchParams.get('type') === 'toc') {
    return <TableOfContentsPreview {...options} />
  }
  return <LessonContentPreview {...options} />
}

function ModuleSummaryPreview({ bleedEnabled, setBleedEnabled }: PreviewBleedSettings) {
  const { bookId } = useParams<{ bookId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const moduleIndex = Number(searchParams.get('moduleIndex') || '0')
  const chapterNumber = Number(searchParams.get('chapterNumber') || String(moduleIndex + 1))
  const moduleName = searchParams.get('moduleName') || '모듈'
  const startPage = Number(searchParams.get('startPage') || '4')
  const lessonsJson = searchParams.get('lessons')
  const moduleLessons: { lessonName: string; pageCount: number | null }[] = lessonsJson
    ? JSON.parse(lessonsJson)
    : []

  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)
  const modBleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const modTotalW = pageWidthPx + modBleedPx * 2
  const modTotalWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const modTotalHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

  const modulePrintRef = useRef<HTMLDivElement>(null)
  const moduleReactToPrintFn = useReactToPrint({
    contentRef: modulePrintRef,
    documentTitle: `Module_${chapterNumber}_${moduleName}`,
    pageStyle: `
      @page { size: ${modTotalWmm}mm ${modTotalHmm}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <FileText size={14} />
          <span>Module {chapterNumber}: {moduleName} · p.{startPage}~{startPage + 1}</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            도련 (3mm)
            <button
              type="button"
              role="switch"
              aria-checked={bleedEnabled}
              onClick={() => setBleedEnabled(!bleedEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                bleedEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <button
            onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>
          <button
            onClick={() => moduleReactToPrintFn()}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
          >
            <Printer size={16} /> PDF 다운로드
          </button>
        </div>
      </div>
      <div className="flex overflow-auto flex-1 justify-center py-6">
        <div
          ref={modulePrintRef}
          className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
          style={{ width: modTotalW }}
        >
          <ModuleSummaryPage
            moduleIndex={moduleIndex}
            chapterNumber={chapterNumber}
            moduleName={moduleName}
            lessons={moduleLessons}
            pageWidth={pageWidthPx}
            pageHeight={pageHeightPx}
            bleedPx={modBleedPx}
          />
        </div>
      </div>
    </div>
  )
}

function TableOfContentsPreview({ bleedEnabled, setBleedEnabled }: PreviewBleedSettings) {
  const { bookId } = useParams<{ bookId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const bookLabelToc = searchParams.get('bookLabel') || ''
  const modulesJson = searchParams.get('modules')
  const rawModules: { moduleName: string; chapterNumber?: number; lessons: { lessonName: string; startPage: number | null; grade?: string; unitInfo?: string }[] }[] =
    modulesJson ? JSON.parse(modulesJson) : []

  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)
  const tocBleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const tocTotalW = pageWidthPx + tocBleedPx * 2
  const tocTotalH = pageHeightPx + tocBleedPx * 2
  const tocTotalWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const tocTotalHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)

  // 더미 학년/단원 데이터
  const DUMMY_GRADES = ['초 5-1', '중 1-1', '고 2-1', '초 4-2', '중 2-2', '초 6-1', '고 3-2', '중 1-2']
  const DUMMY_UNITS = ['4단원. 약분과 통분', '1단원. 소인수분해', '1단원. 지수함수와 로그함수', '2단원. 약수와 배수', '3단원. 소수의 나눗셈', '2단원. 삼각형', '1단원. 공간도형과 공간좌표', '2단원. 도형의 닮음과 피타고라스 정리']

  const tocModules: TocModuleData[] = rawModules.map((mod, mi) => ({
    chapterNumber: mod.chapterNumber ?? (mi + 1),
    moduleName: mod.moduleName,
    colorIndex: Math.min(mi, 2),
    lessons: mod.lessons.map((l, li): TocLesson => ({
      lessonName: l.lessonName,
      startPage: l.startPage,
      grade: l.grade || DUMMY_GRADES[(mi * 3 + li) % DUMMY_GRADES.length],
      unitInfo: l.unitInfo || DUMMY_UNITS[(mi * 3 + li) % DUMMY_UNITS.length],
    })),
  }))

  const tocPrintRef = useRef<HTMLDivElement>(null)
  const tocReactToPrintFn = useReactToPrint({
    contentRef: tocPrintRef,
    documentTitle: `${bookLabelToc}_목차`,
    pageStyle: `
      @page { size: ${tocTotalWmm}mm ${tocTotalHmm}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <FileText size={14} />
          <span>목차 · 2페이지 · p.2~3</span>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            도련 (3mm)
            <button
              type="button"
              role="switch"
              aria-checked={bleedEnabled}
              onClick={() => setBleedEnabled(!bleedEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                bleedEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <button
            onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>
          <button
            onClick={() => tocReactToPrintFn()}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors"
          >
            <Printer size={16} /> PDF 다운로드
          </button>
        </div>
      </div>
      <div className="flex overflow-auto flex-1 justify-center py-6">
        <div
          ref={tocPrintRef}
          className="border border-gray-300 shadow-lg print:shadow-none print:border-0"
          style={{ width: tocTotalW }}
        >
          <style>{`.print-page { overflow: hidden; box-sizing: border-box; height: ${tocTotalH}px; }`}</style>
          <TocPage
            modules={tocModules}
            pageWidth={pageWidthPx}
            pageHeight={pageHeightPx}
            bleedPx={tocBleedPx}
          />
        </div>
      </div>
    </div>
  )
}

function LessonContentPreview({ bleedEnabled, setBleedEnabled }: PreviewBleedSettings) {
  const { bookId, lessonId } = useParams<{ bookId: string; lessonId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const [lessonSettings, setLessonSettings] = useState<PrintBookLessonSettings | null>(null)
  const [settingsInitialized, setSettingsInitialized] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [selectedQuiz, setSelectedQuiz] = useState<{ stepIdx: number; quizIdx: number } | null>(null)


  // ─── 레슨 프리뷰 ──────────────────────────────────────────────────

  // URL search params에서 메타 정보 추출
  const lessonName = searchParams.get('name') || '레슨'
  const startPageNumber = Number(searchParams.get('startPage') || '3')
  const bookLabel = searchParams.get('bookLabel') || ''
  const chapterNumber = searchParams.get('chapter') || '01'
  const grade = searchParams.get('grade') || ''
  const unitLabel = searchParams.get('unitLabel') || ''
  const unitName = searchParams.get('unitName') || ''
  const quetureTitle = searchParams.get('quetureTitle') || lessonName

  // 책 상세에서 레슨 settings 로드
  const { data: bookDetail } = useQuery({
    queryKey: ['print-book', bookId],
    queryFn: () => printBookApi.getById(Number(bookId!)),
    enabled: !!bookId && bookId !== 'new',
  })

  useEffect(() => {
    if (bookDetail && !settingsInitialized) {
      const lesson = bookDetail.modules
        .flatMap(m => m.lessons)
        .find(l => l.lessonId === Number(lessonId))
      const saved = lesson?.printBookLessonSettings
      if (saved) {
        try {
          setLessonSettings(JSON.parse(saved))
        } catch { /* ignore parse errors */ }
      }
      setSettingsInitialized(true)
    }
  }, [bookDetail, settingsInitialized, lessonId])

  const handleSettingsChange = useCallback((newSettings: PrintBookLessonSettings | null) => {
    setLessonSettings(newSettings)
    setIsDirty(true)
  }, [])

  const saveLessonSettingsMutation = useMutation({
    mutationFn: () => printBookApi.updateLessonSettings(
      Number(bookId),
      Number(lessonId),
      lessonSettings
    ),
    onSuccess: () => {
      setIsDirty(false)
      queryClient.invalidateQueries({ queryKey: ['print-book', bookId] })
    },
  })

  const handleSave = useCallback(() => {
    saveLessonSettingsMutation.mutate()
  }, [saveLessonSettingsMutation])

  const handleReset = useCallback(() => {
    if (!window.confirm('모든 커스텀 설정을 기본값으로 초기화하시겠습니까?')) return
    setLessonSettings(null)
    setIsDirty(true)
  }, [])

  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Fetch quizzes by location
  const { data: step1Quizzes = [], isLoading: isLoading1, error: error1 } = useQuery({
    queryKey: ['lesson-quizzes', lessonId, 'selected'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'selected', false, true),
    enabled: !!lessonId,
  })
  const { data: step2Quizzes = [], isLoading: isLoading2, error: error2 } = useQuery({
    queryKey: ['lesson-quizzes', lessonId, 'block'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'block', false, true),
    enabled: !!lessonId,
  })
  const { data: step3Quizzes = [], isLoading: isLoading3, error: error3 } = useQuery({
    queryKey: ['lesson-quizzes', lessonId, 'applied'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'applied', false, true),
    enabled: !!lessonId,
  })
  const { data: step4Quizzes = [], isLoading: isLoading4, error: error4 } = useQuery({
    queryKey: ['lesson-quizzes', lessonId, 'explanation'],
    queryFn: () => quizScriptApi.getContentQuizzes(Number(lessonId), 'explanation', false, true),
    enabled: !!lessonId,
  })

  const isLoading = isLoading1 || isLoading2 || isLoading3 || isLoading4
  const error = error1 || error2 || error3 || error4

  // Step 1~3: usePageSplitter로 페이지 분할 (PrintQuizItem 렌더링)
  const steps = useMemo(
    () => [step1Quizzes, step2Quizzes, step3Quizzes],
    [step1Quizzes, step2Quizzes, step3Quizzes]
  )
  // 측정 대상은 Step 1~3만 (Step 4는 Step4Section으로 별도 렌더링)
  const allQuizzes = useMemo(() => steps.flat(), [steps])
  // Step 4 발문: quiz_script.title 사용
  const step4Questions = useMemo(
    () => step4Quizzes.map(q => q.title || ''),
    [step4Quizzes]
  )
  const invalidQuizzes = useMemo<InvalidQuizEntry[]>(() => {
    const invalid: InvalidQuizEntry[] = []
    let runningNumber = 0
    const stepQuizGroups = [...steps, step4Quizzes]
    stepQuizGroups.forEach((stepQuizzes, stepIdx) => {
      stepQuizzes.forEach(script => {
        runningNumber++
        const issues = getQuizValidationIssues(script)
        if (issues.length === 0) return
        invalid.push({
          lessonId: Number(lessonId ?? 0),
          lessonName,
          stepIdx,
          quizNumberInLesson: runningNumber,
          quizId: String(script.quizId ?? ''),
          issues,
        })
      })
    })
    return invalid
  }, [steps, step4Quizzes, lessonId, lessonName])
  const hasInvalidQuizzes = invalidQuizzes.length > 0

  // Paper dimensions
  const paper = PAPER_SIZES.A4
  const pageWidthPx = mmToPx(paper.width)
  const pageHeightPx = mmToPx(paper.height)
  const bleedPx = bleedEnabled ? mmToPx(BLEED_SIZE_MM) : 0
  const totalPageWidthPx = pageWidthPx + bleedPx * 2
  const totalPageHeightPx = pageHeightPx + bleedPx * 2
  const totalPageWmm = paper.width + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const totalPageHmm = paper.height + (bleedEnabled ? BLEED_SIZE_MM * 2 : 0)
  const gutterPx = 0

  // Measurement
  // 설정 로딩 전에 default 값으로 잘못 측정되어 페이지가 늘어나는 race condition 방지.
  // 저장된 설정이 있는 경우 settingsInitialized=true가 되어 lessonSettings가 적용된 뒤 첫 측정 시작.
  const settingsReady = !bookId || bookId === 'new' || settingsInitialized
  const quizzesForMeasurement = settingsReady ? allQuizzes : EMPTY_QUIZZES
  const { measuredHeights, isMeasuring } = useQuizMeasurement(
    measureRef,
    quizzesForMeasurement,
    ['A4', 'portrait', 1, pageWidthPx, true, bleedEnabled, JSON.stringify(lessonSettings)]
  )
  const stepPages = usePageSplitter(steps, measuredHeights, pageHeightPx)
  const { footerTop } = getPageLayout(pageHeightPx)

  // Step 4 inline check
  const step4FitsInline = useMemo(() => {
    if (stepPages.length === 0) return false
    const lastPage = stepPages[stepPages.length - 1]
    let usedHeight = 0
    if (lastPage.isFirstOfStep) {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.totalHeight + LAYOUT.step1FirstPaddingTop + 51 + LAYOUT.quizTopPadding.afterStepTitle
      } else {
        usedHeight += (LAYOUT.step2.barHeight + LAYOUT.step2.curveHeight) + LAYOUT.quizTopPadding.step23FirstPage
      }
    } else {
      if (lastPage.stepIdx === 0) {
        usedHeight += LAYOUT.header.barHeight + LAYOUT.quizTopPadding.continuationGap
      } else {
        usedHeight += LAYOUT.step2.barHeight + LAYOUT.quizTopPadding.continuationGap
      }
    }
    const globalOffset = steps.slice(0, lastPage.stepIdx).reduce((sum, s) => sum + s.length, 0)
    lastPage.quizzes.forEach((_: QuizScript, i: number) => {
      const quizIdx = globalOffset + (lastPage.quizStartNum - 1) + i
      usedHeight += measuredHeights[quizIdx] || 400
    })
    const footerSpace = 72 + LAYOUT.footer.bottomGap
    const availableSpace = pageHeightPx - usedHeight - footerSpace
    return availableSpace >= STEP4_LAYOUT.inlineHeight
  }, [stepPages, measuredHeights, pageHeightPx, steps])

  // 더미 페이지 필요 여부 (마지막 페이지가 홀수면 더미 삽입 → 다음 레슨이 홀수에서 시작)
  const contentPageCount = useMemo(() => {
    let count = stepPages.length
    if (step4Questions.length > 0 && !step4FitsInline && stepPages.length > 0) count++
    return count
  }, [stepPages, step4FitsInline])

  const lastContentPageNum = startPageNumber + contentPageCount - 1
  const needsMemoPage = lastContentPageNum % 2 === 0 // 짝수에서 끝나면 더미 필요 (다음 레슨이 짝수에서 시작하도록)

  const lessonPageCount = contentPageCount + (needsMemoPage ? 1 : 0)

  const reactToPrintFn = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${bookLabel}_${lessonName}`,
    pageStyle: `
      @page { size: ${totalPageWmm}mm ${totalPageHmm}mm; margin: 0; }
      @media print {
        html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .print-page { page-break-after: always !important; break-after: page !important; position: relative !important; }
        .print-page:last-child { page-break-after: auto !important; break-after: auto !important; }
      }
    `,
  })

  return (
    <div className="flex flex-col h-full">
      {/* Top: Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white sticky top-0 z-10 print:hidden">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <FileText size={14} />
            <span>
              <ContentMetadataText text={lessonName} /> · 시작 p.{startPageNumber} · {lessonPageCount}페이지
              · 퀴즈 S:{step1Quizzes.length} B:{step2Quizzes.length} A:{step3Quizzes.length} E:{step4Quizzes.length}
            </span>
          </div>
          {lessonPageCount > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">|</span>
              <span className="text-slate-500">{lessonPageCount}p</span>
              <span className="text-xs text-slate-400 italic">전체 프리뷰에서 자동 저장</span>
            </div>
          )}
          {hasInvalidQuizzes && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>인쇄 불가 — sample value/answersorder 누락 {invalidQuizzes.length}개</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            도련 (3mm)
            <button
              type="button"
              role="switch"
              aria-checked={bleedEnabled}
              onClick={() => setBleedEnabled(!bleedEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                bleedEnabled ? 'bg-amber-500' : 'bg-gray-300'
              }`}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                bleedEnabled ? 'translate-x-4' : 'translate-x-0'
              }`} />
            </button>
          </label>
          <button
            onClick={() => navigate(`/contents/books/${bookId}?tab=3`)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" /> 돌아가기
          </button>
          <button
            onClick={() => reactToPrintFn()}
            disabled={allQuizzes.length === 0 || isMeasuring || hasInvalidQuizzes}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-slate-900 rounded-lg text-sm font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
          >
            {hasInvalidQuizzes ? (
              <><AlertCircle size={16} /> 인쇄 불가</>
            ) : isMeasuring ? (
              <><Loader2 size={16} className="animate-spin" /> 측정 중...</>
            ) : (
              <><Printer size={16} /> PDF 다운로드</>
            )}
          </button>
        </div>
      </div>

      {/* A4 Preview + Settings Panel */}
      <div className="flex flex-1 items-start">
        <div className="flex-1 flex justify-center py-6">
          <div className="flex flex-col gap-4 items-center">

          {/* Loading */}
          {isLoading && (
            <div className="flex gap-2 items-center py-12 text-slate-500">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">퀴즈 데이터 로딩 중...</span>
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="flex items-center gap-2 p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
              <AlertCircle className="w-5 h-5" />
              퀴즈 데이터를 불러오는데 실패했습니다.
            </div>
          )}

          {hasInvalidQuizzes && (
            <div className="w-full max-w-[860px] p-3 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200 print:hidden">
              <div className="flex items-center gap-2 font-medium">
                <AlertCircle className="w-4 h-4" />
                sample value 또는 answersorder가 등록되지 않은 퀴즈가 있습니다.
              </div>
              <ul className="mt-2 space-y-1 text-xs">
                {invalidQuizzes.map(q => (
                  <li key={`${q.stepIdx}-${q.quizNumberInLesson}-${q.quizId}`}>
                    Step{stepLabelOf(q.stepIdx)} – Q{q.quizNumberInLesson} · quizId: {q.quizId} · 누락: {formatQuizValidationIssues(q.issues)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hidden measurement container */}
          {!isLoading && allQuizzes.length > 0 && (
            <div
              ref={measureRef}
              aria-hidden
              style={{
                position: 'absolute',
                left: -9999,
                top: 0,
                width: `${pageWidthPx - LAYOUT.pageSidePadding * 2 - gutterPx}px`,
                visibility: 'hidden',
                lineHeight: 1.6,
              }}
            >
              {allQuizzes.map((script, i) => {
                let stepIdx = 0, quizIdx = i
                let cumulative = 0
                for (let s = 0; s < steps.length; s++) {
                  if (i < cumulative + steps[s].length) {
                    stepIdx = s
                    quizIdx = i - cumulative
                    break
                  }
                  cumulative += steps[s].length
                }
                const scale = getQuizScale(lessonSettings, stepIdx, quizIdx)
                const spacing = getQuizSpacing(lessonSettings, stepIdx, quizIdx)
                const additionalMap = getQuizAdditionalScalesMap(
                  lessonSettings, stepIdx, quizIdx,
                  toQuizData(script).additionalImages?.length ?? 0,
                )
                const optionScale = getQuizOptionScale(lessonSettings, stepIdx, quizIdx)

                const quizContent = (
                  <QuizRenderer
                    quizType={toQuizData(script).type}
                    quizId={String(script.quizId ?? '')}
                    quiz={toQuizData(script)}
                    mode="print"
                    number={i + 1}
                    hideSubmitButton
                    disableSound
                    compact
                  />
                )
                return (
                  <div key={`measure-${i}`} data-quiz-measure style={{ paddingBottom: `${spacing}px` }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <OptionImageScaleProvider value={optionScale}>
                        <AdditionalImageScalesProvider value={additionalMap}>
                          {scale !== 1
                            ? <ImageScaleMultiplierProvider value={scale}>{quizContent}</ImageScaleMultiplierProvider>
                            : quizContent
                          }
                        </AdditionalImageScalesProvider>
                      </OptionImageScaleProvider>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* A4 Paper Preview */}
          {!isLoading && allQuizzes.length > 0 && (
            <div
              ref={printRef}
              className="bg-white border border-gray-300 shadow-lg print:shadow-none print:border-0"
              style={{ width: `${totalPageWidthPx}px`, lineHeight: 1.6 }}
            >
              <style>{`
                .print-page { overflow: hidden; box-sizing: border-box; height: ${totalPageHeightPx}px; }
              `}</style>

              {stepPages.map((sp, pageIdx) => {
                const pageNumber = startPageNumber + pageIdx
                const isEven = pageNumber % 2 === 0
                const isStep1 = sp.stepIdx === 0
                const isStep1FirstPage = isStep1 && sp.isFirstOfStep

                return (
                  <div key={`page-${pageIdx}`} className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
                    <div className="relative" style={{
                      marginLeft: isEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !isEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      {/* Header */}
                      {isStep1FirstPage ? (
                        <>
                          <PageHeader isEven={isEven} bleedPx={bleedPx} />
                          <ChapterNumber number={chapterNumber} bleedPx={bleedPx} />
                          <TitleQueture
                            grade={grade}
                            unitLabel={unitLabel}
                            unitName={unitName}
                            quetureTitle={lessonSettings?.titleOverride?.trim() ? lessonSettings.titleOverride : quetureTitle}
                            bleedPx={bleedPx}
                          />
                        </>
                      ) : isStep1 ? (
                        <PageHeader isEven={isEven} bleedPx={bleedPx} />
                      ) : (
                        <div className="relative">
                          <StepHeader stepIndex={sp.stepIdx} isEven={isEven} bleedPx={bleedPx} />
                          {sp.isFirstOfStep && <TitleStepSvg stepIndex={sp.stepIdx} isAbsolute bleedPx={bleedPx} />}
                        </div>
                      )}

                      {/* Content */}
                      <div style={{
                        paddingTop: isStep1FirstPage ? LAYOUT.step1FirstPaddingTop + bleedPx : bleedPx,
                        position: 'relative',
                        zIndex: 1,
                      }}>
                        {isStep1FirstPage && <TitleStepSvg stepIndex={0} bleedPx={bleedPx} />}
                        {(() => {
                          let quizPadTop: number
                          let quizMarginTop = 0
                          if (isStep1FirstPage) {
                            quizPadTop = LAYOUT.quizTopPadding.afterStepTitle
                          } else if (sp.isFirstOfStep) {
                            quizPadTop = LAYOUT.quizTopPadding.step23FirstPage
                          } else if (isStep1) {
                            quizPadTop = 0
                            quizMarginTop = -(LAYOUT.header.curveHeight - LAYOUT.quizTopPadding.continuationGap)
                          } else {
                            quizPadTop = LAYOUT.quizTopPadding.continuationGap - LAYOUT.step2.curveHeight
                          }
                          return (
                            <div style={{
                              paddingTop: quizPadTop,
                              marginTop: quizMarginTop,
                              paddingLeft: LAYOUT.pageSidePadding + bleedPx,
                              paddingRight: LAYOUT.pageSidePadding + bleedPx,
                            }}>
                              {sp.quizzes.map((script: QuizScript, i: number) => {
                                const quizIdx = sp.quizStartNum - 1 + i
                                const scale = getQuizScale(lessonSettings, sp.stepIdx, quizIdx)
                                const spacing = getQuizSpacing(lessonSettings, sp.stepIdx, quizIdx)
                                const additionalMap = getQuizAdditionalScalesMap(
                                  lessonSettings, sp.stepIdx, quizIdx,
                                  toQuizData(script).additionalImages?.length ?? 0,
                                )
                                const optionScale = getQuizOptionScale(lessonSettings, sp.stepIdx, quizIdx)
                                const isSelected = selectedQuiz?.stepIdx === sp.stepIdx && selectedQuiz?.quizIdx === quizIdx
                                return (
                                  <PrintQuizItem
                                    key={`${sp.stepIdx}-${sp.quizStartNum + i}`}
                                    script={script}
                                    number={sp.quizStartNum + i}
                                    stepIdx={sp.stepIdx}
                                    compact
                                    imageScale={scale}
                                    additionalImageScales={additionalMap}
                                    optionImageScale={optionScale}
                                    spacingOverride={spacing}
                                    highlighted={isSelected}
                                    onSelect={() => setSelectedQuiz({ stepIdx: sp.stepIdx, quizIdx })}
                                  />
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>

                      {/* Step 4 inline */}
                      {pageIdx === stepPages.length - 1 && step4FitsInline && step4Questions.length > 0 && (
                        <div style={{ position: 'absolute', bottom: totalPageHeightPx - (footerTop + bleedPx) + 20, left: bleedPx, right: bleedPx }}>
                          <Step4Section questions={step4Questions} />
                        </div>
                      )}

                      {/* Footer */}
                      {<PageFooter pageNumber={pageNumber} isEven={isEven} bookLabel={bookLabel} footerTop={footerTop} bleedPx={bleedPx} />}
                    </div>
                  </div>
                )
              })}

              {/* Step 4 separate page */}
              {!step4FitsInline && step4Questions.length > 0 && stepPages.length > 0 && (() => {
                const step4PageNumber = startPageNumber + stepPages.length
                const step4IsEven = step4PageNumber % 2 === 0
                return (
                  <div className="print-page relative" style={{ width: `${totalPageWidthPx}px` }}>
                    <div className="relative" style={{
                      marginLeft: step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      marginRight: !step4IsEven && gutterPx > 0 ? gutterPx : 0,
                      height: totalPageHeightPx,
                    }}>
                      <div className="relative">
                        <StepHeader stepIndex={3} isEven={step4IsEven} bleedPx={bleedPx} />
                        <TitleStepSvg stepIndex={3} isAbsolute bleedPx={bleedPx} />
                      </div>
                      <div style={{ paddingTop: LAYOUT.quizTopPadding.step23FirstPage + bleedPx, paddingLeft: bleedPx, paddingRight: bleedPx }}>
                        <Step4Section questions={step4Questions} showTitle={false} />
                      </div>
                      <PageFooter pageNumber={step4PageNumber} isEven={step4IsEven} bookLabel={bookLabel} footerTop={footerTop} bleedPx={bleedPx} />
                    </div>
                  </div>
                )
              })()}
              {/* Memo page */}
              {needsMemoPage && (
                <MemoPage
                  pageNumber={lastContentPageNum + 1}
                  pageWidth={pageWidthPx}
                  pageHeight={pageHeightPx}
                  bleedPx={bleedPx}
                />
              )}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && allQuizzes.length === 0 && (
            <div className="py-12 text-sm text-center text-slate-400">이 레슨에 퀴즈가 없습니다.</div>
          )}
          </div>
        </div>

        {/* Settings Panel */}
        {!isLoading && allQuizzes.length > 0 && (
          <LessonPreviewSettingsPanel
            stepQuizzes={steps}
            settings={lessonSettings}
            isDirty={isDirty}
            isSaving={saveLessonSettingsMutation.isPending}
            selectedQuiz={selectedQuiz}
            originalTitle={quetureTitle}
            onSettingsChange={handleSettingsChange}
            onSave={handleSave}
            onReset={handleReset}
            onSelectQuiz={(stepIdx, quizIdx) => setSelectedQuiz({ stepIdx, quizIdx })}
          />
        )}
      </div>
    </div>
  )
}
